import {
	ApiClient,
	CollectionEnvelope,
	Region,
	notificationService,
	platformService,
	trackingService,
} from '@monthio/react-components'
import dayjs from 'dayjs'
import { ClientInputs } from '../types'
import {
	AuthenticateSmartcheckSessionDto,
	ProcessingStatus,
	SmartCheckModuleType,
} from '../types/authenticateSmartcheckSessionDto'
import { ConsumerBudgetOutputResponseDto } from '../types/consumerBudgetOutputResponseDto'
import {
	BudgetSubmitPayload,
	EstimatesResponseDto,
	FileMetadataDto,
	TransactionRequestDto,
	V2BudgetData,
} from '../Modules/Budget/types/budgetApi.types'
import { getInputName, getOutputName } from './helperFunc/adaMapping'
import { getNationalities } from './helperFunc/allNationalities'
import {
	DefaultConsumerInputs,
	transformToRequestDto,
} from '#modules/Questionnaire/types/questionnaire.types'
import { CprResponse } from '#modules/Questionnaire/Components/personalInformation/validators/types'
import {
	CreditorDto,
	DebtOverviewModuleState,
	DebtRecordEstimatesRevampedDto,
	DebtRecordRevamped,
	RevampedDebtRecordDto,
} from '#modules/DebtOverview/types/debtOverviewApi.types'
import { DebtItem } from '#modules/DebtOverview/Components/debtItem'
import {
	SavingsCapacityExtraordinaryItemGroupDto,
	TransactionOutputApplicantTransactionDto,
} from '#modules/SavingsCapacity/SavingsCapacity.types'
import {
	CategorizedDocument,
	IncomeVerificationFormData,
	IncomeVerificationResponseDto,
} from '#modules/IncomeVerification/IncomeVerification.types'
import { SendBudgetHelperPdfDto } from '#modules/BudgetConsumerEmail/BudgetConsumerEmail.types'
import { Asset, RealEstateFinancingType } from '#modules/AssetOverview/AssetOverview.types'
import {
	AssetCategory,
	ExpenseEstimatesDto,
	ExpenseEstimatesPostDto,
} from '#modules/Budget/types/expense.types'
import {
	AssetBudgetPostDto,
	AssetBudgetsDto,
} from '#modules/AssetExpenses/AssetExpenses.types'
import { ApplicantDto, ApplicantSessionDto } from '#modules/Eskat/Eskat.types'
import { InnerError } from '../types/error'
import {
	CustomQuestionnaireConfigurationDto,
	CustomQuestionnaireAnswersPayload,
} from './customQuestionnaire'

class BudgetApiClient {
	private apiClient = null as unknown as ApiClient
	private applicantDataApiClient = null as unknown as ApiClient
	private transactionEnrichmentApiClient = null as unknown as ApiClient
	private whiteLabelPairedCaseResponse: WhitelabelSubdomainCase | null = null
	private cprResponses = new Map<string, CprResponse>()

	public initialize = () => {
		this.apiClient = platformService.createApiClient(
			platformService.getAppUrl('budgetApp'),
			{ appendApi: true },
		)

		this.applicantDataApiClient = platformService.createApiClient(
			platformService.getAppUrl('applicantDataApp'),
			{ appendApi: true },
		)

		this.transactionEnrichmentApiClient = platformService.createApiClient(
			platformService.getAppUrl('transactionEnrichmentApp'),
			{ appendApi: true },
		)
	}

	public authenticateSessionWithApplicant = async (applicantId: string) => {
		try {
			const response = await this.apiClient
				.useCredentials('include')
				.timeout(false)
				.get<AuthenticateSmartcheckSessionDto>(
					`smart-check-sessions/${applicantId}/authenticate`,
				)

			return response
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public authenticateSessionWithApplicantV2 = async (applicantId: string) => {
		try {
			const response = await this.applicantDataApiClient
				.useCredentials('include')
				.timeout(3_000)
				.get<AuthenticateSmartcheckSessionDto>(
					`${applicantId}/authenticate`,
				)

			return response
		} catch (error: any) {
			return Promise.reject(this.applicantDataApiClient.mapError(error))
		}
	}

	public deleteSmartCheckSession = async () => {
		try {
			trackingService.trackTechnicalEvent('SmartCheckSessionDeleted')
			await Promise.all([
				this.apiClient
					.timeout(false)
					.useCredentials('include')
					.retry(3)
					.delete<void>(`smart-check-sessions`),
				this.applicantDataApiClient
					.timeout(10_000)
					.delete<void>(`session`)
					.catch(() => {
						// ignore any async errors safely
					}),
			])
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public trackFlowProgressEvent = async (
		module: string,
		status: 'started' | 'completed',
	) => {
		try {
			await this.apiClient
				.timeout(false)
				.useCredentials('include')
				.retry(3)
				.keepAlive()
				.postRaw(`v1/case/flow-progress-status`, {
					body: JSON.stringify({ module, status }),
				})
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public finishFlowModule = async (
		module: string,
		data: Record<string, unknown> | null,
	) => {
		try {
			await this.apiClient
				.timeout(false)
				.useCredentials('include')
				.retry(3)
				.keepAlive()
				.postRaw(`v1/case/flow-module-finished`, {
					body: JSON.stringify({ module, data }),
				})
			return true
		} catch (error: any) {
			return false
		}
	}

	public finishFlowModuleWithApplicantDataApi = async (
		module: string,
		data: Record<string, unknown> | null,
	) => {
		try {
			let dataWithTypeDescriminatorIfApplicable = data

			if (data != null) {
				dataWithTypeDescriminatorIfApplicable = {
					$type: GetOutputName(module),
					...data,
				}
			}

			await this.applicantDataApiClient
				.timeout(false)
				.useCredentials('include')
				.keepAlive()
				.postRaw(`session/finish-module-flow?moduleId=${module}`, {
					body: JSON.stringify({
						module,
						data: dataWithTypeDescriminatorIfApplicable,
					}),
				})
			return true
		} catch (error: any) {
			return false
		}
	}

	public fetchDemoProfiles = async (): Promise<DemoProfileDto[]> => {
		try {
			return await this.apiClient.get<DemoProfileDto[]>('demo-profiles')
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getNationalities = async (): Promise<Nationality[]> => {
		return getNationalities()
	}

	public getSubdomainData = async () => {
		try {
			const response = await this.apiClient
				.timeout(false)
				.retry(3)
				.getRaw('whitelabel-subdomains/data', {
					headers: this.getHeaderForSubdomain(),
				})
				.json<WhiteLabelSubdomainInfo>()

			return response
		} catch (error) {
			return false
		}
	}

	public createCaseWithSubdomainInfo = async (
		paired = false,
		sameDevice = false,
		clientInputs: ClientInputs | null = null,
		useHostRedirect: boolean = false,
	) => {
		const pairedCaseSeparateDevice = paired && !sameDevice
		if (pairedCaseSeparateDevice && this.whiteLabelPairedCaseResponse != null) {
			return this.whiteLabelPairedCaseResponse
		}

		try {
			const response = await this.apiClient
				.timeout(false)
				.retry(3)
				.postRaw('whitelabel-subdomains/case', {
					headers: this.getHeaderForSubdomain(),
					body: JSON.stringify({ paired, clientInputs }),
				})
				.json<WhitelabelSubdomainCase>()
			const params = new URLSearchParams()
			params.append(
				'sessionId',
				sameDevice && response.secondaryApplicantId != null
					? response.secondaryApplicantId
					: response.mainApplicantId,
			)
			const redirectPath = useHostRedirect ? '/' : location.pathname
			window.history.replaceState(
				{},
				document.title,
				`${redirectPath}?${params}`,
			)
			if (pairedCaseSeparateDevice) {
				this.whiteLabelPairedCaseResponse = response
			}
			return response
		} catch (error) {
			trackingService.trackError(
				error as Error,
				'failed to create case with subdomain',
			)
			notificationService.error()
		}
	}

	public getConsumerBudgetOutput = async () => {
		try {
			const response = await this.apiClient
				.useCredentials('include')
				.timeout(false)
				.get<ConsumerBudgetOutputResponseDto>('budget-outputs/consumer')

			return response
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public createIncorrectTransaction = async (
		transaction: TransactionRequestDto,
	): Promise<void> => {
		try {
			await this.apiClient.post('incorrect-transactions', transaction)
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public postCaseProcessingStatus = async (
		caseId: string,
		status: ProcessingStatus,
	): Promise<void> => {
		try {
			await this.apiClient.post(`v1/case/${caseId}/processing-status`, {
				status,
			})
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public uploadFiles = async (
		files: File[],
	): Promise<{ fileId: string; fileName: string; url: string }[]> => {
		const formData = new FormData()
		files.forEach((file) => formData.append('uploadedFiles', file, file.name))

		return await this.uploadRawFiles(formData)
	}

	public uploadWithCategories = async (
		files: CategorizedDocument[],
	): Promise<{ fileId: string; fileName: string; url: string }[]> => {
		const formData = new FormData()
		files.forEach(([file, _]) =>
			formData.append('uploadedFiles', file, file.name),
		)

		formData.append(
			'documentTypes',
			JSON.stringify(files.map(([_, type]) => type)),
		)

		return await this.uploadRawFiles(formData)
	}

	private uploadRawFiles = async (
		formData: FormData,
	): Promise<FileMetadataDto[]> => {
		try {
			return await this.apiClient
				.timeout(false)
				.retry(3)
				.postRaw(`case/files`, {
					body: formData,
				})
				.json()
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getBudgetEstimates = async (): Promise<EstimatesResponseDto> => {
		try {
			const data =
				await this.apiClient.get<EstimatesResponseDto>('estimates/budget')

			// for debug purposes removing transactions as they can be up to 10mb
			const { budgetCategorizedTransactions, ...clone } = data
			await this.callApplicantDataAppStartModule('budget', clone)
			return data
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public finishBudgetModule = async (
		data: BudgetSubmitPayload,
	): Promise<void> => {
		if (data.uploadedFiles != null && data.uploadedFiles.length > 0) {
			this.uploadFiles(data.uploadedFiles)
		}

		const estimates = {
			...data.estimates,
			...(data.formData as any),
		}

		try {
			await Promise.all([
				this.apiClient.put<EstimatesResponseDto>('estimates/budget', estimates),
				this.callApplicantDataAppFinishModule('budget', {
					corrections: estimates,
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	private getHeaderForSubdomain = () => {
		const origin = location.host

		return {
			'x-origin': origin,
		}
	}

	public getDebtOverviewEstimates =
		async (): Promise<DebtOverviewModuleState> => {
			const { debtRecords, debtPayments, allDebtPayments } =
				await budgetApiClient.fetchDebtRecords()

			let creditors: CreditorDto[] = []
			try {
				creditors = (await this.fetchCreditors(debtRecords)) ?? []
			} catch (error) {
				creditors = []
			}
			return {
				debtRecords: debtRecords,
				debtPayments: debtPayments,
				allDebtPayments: allDebtPayments || [],
				creditors: creditors,
			}
		}

	private fetchCreditors = async (
		debtRecords: RevampedDebtRecordDto[],
	): Promise<CreditorDto[] | undefined> => {
		const creditorIds = Array.from(
			debtRecords.reduce((creditorIds, record) => {
				const creditorId =
					record.monthioCreditorId !== null ? record.monthioCreditorId : NaN
				if (!isNaN(creditorId)) {
					creditorIds.add(creditorId)
				}
				return creditorIds
			}, new Set<number>()),
		)

		try {
			return await this.transactionEnrichmentApiClient
				.retry(3)
				.post('creditors/consumer', {
					creditorIds,
				})
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	private fetchDebtRecords =
		async (): Promise<DebtRecordEstimatesRevampedDto> => {
			try {
				// here will be fire and forget start module for ada
				const data = await this.apiClient
					.retry(3)
					.post<DebtRecordEstimatesRevampedDto>('debt-record')
				await this.callApplicantDataAppStartModule('debtOverview', data)
				return data
			} catch (error) {
				return Promise.reject(this.apiClient.mapError(error))
			}
		}
	public finishDebtOverviewModule = async (
		updated: DebtItem[],
	): Promise<void> => {
		function canonicalDebtRecord(debtItem: DebtItem): DebtRecordRevamped {
			return debtItem.debtRecords[0]
		}

		const data = {
			debtRecords: updated.flatMap((x) =>
				x.debtRecords.map((y) => ({
					...y,
					status: canonicalDebtRecord(x).status ?? y.status,
					paymentId: x.debtPayment.id,
				})),
			),
			debtPayments: updated.map((x) => x.debtPayment),
		}

		try {
			await Promise.all([
				this.apiClient.retry(3).put('debt-record', data),
				this.callApplicantDataAppFinishModule('debtOverview', {
					corrections: data,
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public finishQuestonnaireModule = async (
		rawData: DefaultConsumerInputs,
	): Promise<void> => {
		try {
			if (rawData.uploadedFiles != null && rawData.uploadedFiles.length > 0) {
				await this.uploadFiles(rawData.uploadedFiles)
			}

			const data = transformToRequestDto(rawData)
			// Typed on back-end to receive a generic object
			await Promise.all([
				this.apiClient.post('budget-outputs/consumer-inputs', data),
				this.callApplicantDataAppFinishModule('questionnaire', {
					questions: data,
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getCustomQuestionnaireQuestions =
		async (): Promise<CustomQuestionnaireConfigurationDto> => {
			try {
				const data = await this.apiClient
					.retry(3)
					.get<CustomQuestionnaireConfigurationDto>('custom-questionnaire-configuration')
				await this.callApplicantDataAppStartModule('customQuestionnaire', data)
				return data
			} catch (error) {
				return Promise.reject(this.apiClient.mapError(error))
			}
		}

	public finishCustomQuestionnaireModule = async (
		payload: CustomQuestionnaireAnswersPayload,
	): Promise<void> => {
		try {
			const payloadForBudgetApp = {
				...payload,
				answers: payload.answers.map(({ '$type': _, ...answer }) => answer),
			}
			await Promise.all([
				this.apiClient.post('custom-questionnaire', payloadForBudgetApp),
				this.callApplicantDataAppFinishModule('customQuestionnaire', {
					answers: payload.answers,
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public validateCprAgainstServer = async (inputs: string) => {
		const cachedResponse = this.cprResponses.get(inputs)
		if (cachedResponse != null) {
			return cachedResponse
		}
		try {
			await this.validateCpr(inputs)

			const result = { isValid: true }
			this.cprResponses.set(inputs, result)
			return result
		} catch (error: Error | any) {
			if (error.status === 401) {
				return { isValid: false, error: 'Unauthorized' as any }
			}
			const monthioError = this.apiClient.mapError(error)

			const result = {
				isValid: false,
				error: monthioError?.data?.validationErrors?.[0] as InnerError,
			}
			this.cprResponses.set(inputs, result)
			return result
		}
	}

	private validateCpr = async (inputs: string): Promise<void> => {
		try {
			return this.apiClient.post<void>(
				'budget-outputs/consumer-inputs/cpr-validation',
				{ test: inputs },
			)
		} catch (error) {
			const monthioError = this.apiClient.mapError(error)
		}
	}

	public getSessionTransactionsByCategories = async (
		categoryIds: number[],
		amountFloor?: number,
	): Promise<CollectionEnvelope<TransactionOutputApplicantTransactionDto>> => {
		try {
			let requestUrl = 'v1/case/transactions'
			if (categoryIds.length > 0) {
				const categoryFilter = categoryIds
					.map((id) => `categoryId eq ${id}`)
					.join(' or ')
				const filter =
					amountFloor === undefined
						? categoryFilter
						: `( ${`amount ${amountFloor >= 0 ? 'ge' : 'le'} ${amountFloor}`} ) and ( ${categoryFilter} )`

				requestUrl += `?$filter=${encodeURIComponent(filter)}&$pagesize=10000`
			}

			return await this.apiClient.get(requestUrl)
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getIncomeVerification =
		async (): Promise<IncomeVerificationResponseDto> => {
			try {
				const data = await this.apiClient.post<IncomeVerificationResponseDto>(
					'income-verification',
				)
				await this.callApplicantDataAppStartModule('incomeVerification', data)
				return data
			} catch (error) {
				return Promise.reject(this.apiClient.mapError(error))
			}
		}

	public finishIncomeVerification = async (
		data: IncomeVerificationFormData,
	): Promise<void> => {
		if (data.categorizedDocuments?.length) {
			const uploadPromises = data.categorizedDocuments.map((doc) =>
				this.uploadWithCategories([doc]),
			)

			await Promise.all(uploadPromises)
		}

		if (data.uploadedFiles?.length) {
			const uploadPromises = data.uploadedFiles.map((doc) =>
				this.uploadFiles([doc]),
			)

			await Promise.all(uploadPromises)
		}

		try {
			await Promise.all([
				this.apiClient.put('income-verification', data),
				this.callApplicantDataAppFinishModule('incomeVerification', data),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public finishSavingsCapacity = async (
		data: SavingsCapacityExtraordinaryItemGroupDto[],
	): Promise<void> => {
		const extraordinaryItemGroupsToSubmit = data.map((group) => ({
			...group,
			items: group.items.map((item) => {
				return {
					...item,
					startDate:
						item.type == 'manual'
							? dayjs(item.startDate).format('YYYY-MM-DD')
							: null,
					endDate:
						item.type == 'manual' && !!item.endDate
							? dayjs(item.endDate).format('YYYY-MM-DD')
							: null,
					transactionId: item.type == 'transaction' ? item.transactionId : null,
					transactionDate:
						item.type == 'transaction' ? item.transactionDate : null,
					amount: -Math.abs(item.amount),
				}
			}),
		}))

		await budgetApiClient.submitExtraordinaryItemGroups(
			extraordinaryItemGroupsToSubmit,
		)
	}

	public finishSession = async () => {
		trackingService.trackTechnicalEvent('ClosingSmartCheckSession')
		try {
			// here will be fire and forget finish session for ada
			await this.apiClient.retry(3).put('smart-check-sessions/finish')
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}

		trackingService.trackTechnicalEvent('SmartCheckSessionClosed')
	}

	public fetchAssets = async (waitForTinglysning = false): Promise<Asset[]> => {
		if (waitForTinglysning) {
			// wait and hope tinglysnings has finished
			// TODO: this is a hack, we should have a better way to know when tinglysnings has finished
			await new Promise((res) => setTimeout(res, 10000))
		}

		try {
			// here will be fire and forget start module for ada
			const assets = await this.apiClient
				.retry(3)
				.get<any[]>('expense-objects-overview')
			await this.callApplicantDataAppStartModule('assetOverview', { items: assets })

			return assets
				.map(({ json, type, ownershipType, ...asset }) => ({
					...asset,
					...JSON.parse(json || '{}'),
					financingType: ownershipType,
					assetType: type,
				}))
				.map((asset) => {
					if ('year' in asset && typeof asset.year !== 'number') {
						asset.year = Number(asset.year)
					}
					return asset
				})
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public updateAssets = async (assets: Asset[]): Promise<void> => {
		try {
			await Promise.all([
				this.apiClient
					.retry(3)
					.put<any[]>('expense-objects-overview', mapAssets(assets)),
				this.callApplicantDataAppFinishModule('assetReplacement', {
					corrections: mapAssets(assets),
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public createAssets = async (assets: Asset[]): Promise<void> => {
		try {
			await Promise.all([
				this.apiClient
					.retry(3)
					.post<any[]>('expense-objects-overview', mapAssets(assets)),
				this.callApplicantDataAppFinishModule('assetOverview', {
					corrections: mapAssets(assets),
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	validateAssets = async (
		assets: any[],
	): Promise<{ isValid: boolean; error: any }> => {
		try {
			await this.apiClient
				.retry(3)
				.post<any[]>('expense-objects-overview/validate', mapAssets(assets))
			return { isValid: true, error: undefined }
		} catch (error: any) {
			const monthioError = this.apiClient.mapError(error)
			if (
				monthioError.status === 400 &&
				monthioError.data.validationErrors?.length > 0
			) {
				return {
					isValid: false,
					error: monthioError.data.validationErrors[0],
				}
			}
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public updateAssetExpenseEstimates = async (
		assignments: ExpenseEstimatesPostDto[],
		assetCategory: AssetCategory,
	): Promise<any> => {
		try {
			const [data] = await Promise.all([
				this.apiClient
					.retry(3)
					.put(`asset/expense-estimates/${assetCategory}`, {
						assignments,
					}),
				this.callApplicantDataAppFinishModule('assetExpenses', {
					expensePart: 'assignments',
					assignments,
					assetCategory,
				}),
			])
			return data
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getAssetBudgets = async (
		assetCategory: AssetCategory,
	): Promise<AssetBudgetsDto> => {
		try {
			const data = await this.apiClient
				.retry(3)
				.get<AssetBudgetsDto>(`asset/asset-budgets/${assetCategory}`)
			await this.callApplicantDataAppStartModule('assetExpenses', {
				data,
				expensePart: 'budget',
				assetCategory,
			})
			return data
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public updateAssetBudgets = async (
		assetBudgets: AssetBudgetPostDto[],
		assetCategory: AssetCategory,
	): Promise<any> => {
		try {
			const [data] = await Promise.all([
				this.apiClient.retry(3).put(`asset/asset-budgets/${assetCategory}`, {
					assetBudgets,
				}),
				this.callApplicantDataAppFinishModule('assetExpenses', {
					expensePart: 'budget',
					assetBudgets,
					assetCategory,
				}),
			])
			return data
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getAssetExpenseEstimates = async (
		assetCategory: AssetCategory,
	): Promise<ExpenseEstimatesDto> => {
		try {
			const data = await this.apiClient
				.retry(3)
				.get<ExpenseEstimatesDto>(`asset/expense-estimates/${assetCategory}`)
			await this.callApplicantDataAppStartModule('assetExpenses', {
				data,
				expensePart: 'assignments',
				assetCategory,
			})
			return data
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	private submitExtraordinaryItemGroups = async (
		extraordinaryItemGroups: any[],
	): Promise<void> => {
		try {
			await Promise.all([
				this.apiClient
					.retry(3)
					.post(
						'savings-capacity/extraordinary-item-groups',
						extraordinaryItemGroups,
					),
				this.callApplicantDataAppFinishModule('savingsCapacity', {
					groups: extraordinaryItemGroups,
				}),
			])
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public sendBudgetHelperPdf = async (
		payload: SendBudgetHelperPdfDto,
	): Promise<void> => {
		try {
			await this.apiClient.post(`budget-helper/send-email`, payload)
		} catch (error) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getApplicant = async () => {
		try {
			return await this.apiClient.get<ApplicantDto>('applicant')
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getApplicantSession = async (applicantSessionId: string) => {
		try {
			return await this.apiClient.get<ApplicantSessionDto>(
				`applicant/session/${applicantSessionId}`,
			)
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public getApplicantsByCaseId = async (caseId: string) => {
		try {
			return await this.apiClient.get<ApplicantDto[]>(`applicant/${caseId}`)
		} catch (error: any) {
			return Promise.reject(this.apiClient.mapError(error))
		}
	}

	public async callApplicantDataAppStartModule(moduleId: string, data: any): Promise<void> {
		try {
			return await this.applicantDataApiClient
				.timeout(5_000)
				.post(`session/debug-start-module-flow?moduleId=${moduleId}`, {
					data: data != null ? { ...data, $type: getInputName(moduleId) } : null,
				})
				.catch(() => {
					// ignore any async errors safely
				})
		} catch {
			// intentionally ignored
		}
	}

	private async callApplicantDataAppFinishModule(
		moduleId: string,
		data: any,
	): Promise<void> {
		try {
			return await this.applicantDataApiClient
				.timeout(10_000)
				.post(`session/finish-module-flow?moduleId=${moduleId}`, {
					data: { ...data, $type: getOutputName(moduleId) },
				})
		} catch {
			// intentionally ignored
		}
	}
}

export interface WhiteLabelSubdomainInfo {
	clientName: string
	clientId: number
	logo?: string
	region?: Region
	whitelabelSubdomainTranslations: WhitelabelSubdomainTranslation[]
	canCreatePairedCases: boolean
	canRunPairedCasesOnSameDevice: boolean
	type: WhiteLabelSubdomainType
}

export interface WhitelabelSubdomainTranslation {
	translationHeader: string
	translationBody: string
	translationPairingChoice: string
	languageCode: string
}

export interface WhitelabelSubdomainCase {
	caseId: string
	mainApplicantId: string
	mainApplicantFlowUrl: string
	secondaryApplicantId?: string
	secondaryApplicantFlowUrl?: string
}

export enum WhiteLabelSubdomainType {
	Default = 'default',
	BudgetHelper = 'budgetHelper',
}

export interface DemoProfileDto {
	name: string
	cpr?: string
}

export type Nationality = { code: string; name: string }

export const budgetApiClient = new BudgetApiClient()

// temp, we will get version of module from backend and then just pass it here
function GetOutputName(module: string) {
	switch (module) {
		case SmartCheckModuleType.Budget:
			return 'BudgetOutput-v1'
		case SmartCheckModuleType.DebtOverview:
			return 'DebtOverviewOutput-v1'
		case SmartCheckModuleType.IncomeVerification:
			return 'IncomeVerificationOutput-v1'
		case SmartCheckModuleType.Questionnaire:
			return 'QuestionnaireOutput-v1'
		default:
			return module
	}
}

const mapAssets = (assets: Asset[]) =>
	assets.map(
		({
			id,
			description,
			value,
			currency,
			deletedByApplicant,
			addedByApplicant,
			excludedFromFutureBudget,
			documentationLink,
			assetType,
			financingType,
			valuation,
			assetValueAddedByApplicant,
			...assetTypeSpecificData
		}) => ({
			id,
			description,
			value,
			currency,
			deletedByApplicant,
			addedByApplicant,
			excludedFromFutureBudget,
			documentationLink,
			ownershipType: financingType,
			type: assetType,
			...(financingType === RealEstateFinancingType.Cooperative && assetValueAddedByApplicant !== undefined
				? { assetValueAddedByApplicant: assetValueAddedByApplicant }
				: {}),
			json: JSON.stringify(assetTypeSpecificData),
		}),
	)
